import { useMutation, useQueryClient } from '@tanstack/react-query'

/** Status envelope the removegroup callable resolves with (it does not throw on validation errors). */
export interface DeleteGroupResponse {
  status: 'SUCCESS' | 'ERROR'
  message?: string
}

/**
 * The callable resolves a `{ status }` envelope rather than throwing on
 * failure (e.g. a non-owner gets `ERROR`), so unwrap it into either success
 * or a thrown error the mutation layer can surface.
 */
export function unwrapDeleteGroupResponse(data: DeleteGroupResponse): void {
  if (data.status !== 'SUCCESS') {
    throw new Error(data.message ?? 'Could not delete the team. Please try again.')
  }
}

/**
 * Deletes a whole team via the `removegroup` Firebase callable (europe-west1).
 *
 * The name is ALL LOWERCASE on the backend. The camelCase `deleteGroup` names
 * no deployed function, so every delete 404'd and surfaced as an error toast —
 * which read as "the backend was never built" rather than "the name is wrong".
 * Mobile hit the identical trap and left the same warning on its own constant.
 *
 * Only the team Owner may delete it, and `removegroup` enforces that itself
 * (a non-owner gets an ERROR envelope, not a silent success); the UI
 * additionally hides the action from non-owners. On success the my-groups list
 * is invalidated so the deleted team disappears.
 *
 * Mirrors {@link useCreateGroup}: the Functions SDK is imported lazily to keep
 * it off the synchronous bundle path.
 */
export function useDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (groupId: string): Promise<void> => {
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<{ groupId: string }, DeleteGroupResponse>(fns, 'removegroup')
      const res = await call({ groupId })
      unwrapDeleteGroupResponse(res.data)
    },
    // Await the refetch so a caller that navigates to the Teams list on success
    // lands after the my-groups list has dropped the deleted team.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'groups'] })
    },
  })
}
